import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const FeedPost = ({ post, currentUser, onInteraction }) => {
  const [showComments, setShowComments] = useState(false);
  const [comment, setComment] = useState('');
  const [isLiked, setIsLiked] = useState(false);

  if (!post) return null;

  const author = post?.author;
  const authorPartner = post?.author_partner;

  const getPostTypeIcon = () => {
    switch (post?.post_type) {
      case 'achievement': return 'Trophy';
      case 'performance_announcement': return 'Music';
      case 'collaboration': return 'Handshake';
      case 'career_milestone': return 'Star';
      case 'industry_news': return 'Newspaper';
      default: return 'FileText';
    }
  };

  const getPostTypeLabel = () => {
    switch (post?.post_type) {
      case 'achievement': return 'Réussite';
      case 'performance_announcement': return 'Annonce de performance';
      case 'collaboration': return 'Collaboration';
      case 'career_milestone': return 'Étape carrière';
      case 'industry_news': return 'Actualité industrie';
      default: return 'Publication';
    }
  };

  const getPostTypeColor = () => {
    switch (post?.post_type) {
      case 'achievement': return 'text-yellow-600';
      case 'performance_announcement': return 'text-purple-600';
      case 'collaboration': return 'text-blue-600';
      case 'career_milestone': return 'text-green-600';
      case 'industry_news': return 'text-orange-600';
      default: return 'text-muted-foreground';
    }
  };

  const formatTimeAgo = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), {
        addSuffix: true,
        locale: fr
      });
    } catch {
      return 'récemment';
    }
  };

  const handleInteractionClick = (interactionType) => {
    onInteraction?.(post?.id, interactionType);
    if (interactionType === 'like') {
      setIsLiked(!isLiked);
    }
  };

  const getAuthorName = () => {
    return authorPartner?.business_name || author?.full_name || 'Utilisateur anonyme';
  };

  const getAuthorRole = () => {
    if (authorPartner?.partner_type) {
      const roles = {
        'agent': 'Agent',
        'venue': 'Venue',
        'booker': 'Booker',
        'independent': 'Artiste indépendant'
      };
      return roles?.[authorPartner?.partner_type] || authorPartner?.partner_type;
    }
    return author?.role || 'Membre';
  };

  const getAuthorProfession = () => {
    if (authorPartner?.artist_profession) {
      const professions = {
        'musician': 'Musicien',
        'singer': 'Chanteur',
        'dj': 'DJ',
        'producer': 'Producteur',
        'model': 'Modèle',
        'actor': 'Acteur',
        'dancer': 'Danseur',
        'performer': 'Artiste'
      };
      return professions?.[authorPartner?.artist_profession] || authorPartner?.artist_profession;
    }
    return null;
  };

  return (
    <div className="bg-surface rounded-lg border border-border overflow-hidden">
      {/* Post Header */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            {/* Author Avatar */}
            <Link to={`/enhanced-artist-profile`} className="flex-shrink-0">
              {author?.avatar_url ? (
                <img
                  src={author?.avatar_url}
                  alt={getAuthorName()}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <span className="text-primary-foreground font-semibold text-sm">
                    {getAuthorName()?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
              )}
            </Link>

            {/* Author Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <Link 
                  to={`/enhanced-artist-profile`}
                  className="font-semibold text-foreground hover:text-accent transition-colors"
                >
                  {getAuthorName()}
                </Link>
                {authorPartner?.is_verified && (
                  <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                    <Icon name="Check" size={10} color="white" />
                  </div>
                )}
              </div>
              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <span>{getAuthorRole()}</span>
                {getAuthorProfession() && (
                  <>
                    <span>•</span>
                    <span>{getAuthorProfession()}</span>
                  </>
                )}
                <span>•</span>
                <span>{formatTimeAgo(post?.created_at)}</span>
              </div>
            </div>
          </div>

          {/* Post Type Badge */}
          <div className={`flex items-center space-x-1 px-2 py-1 rounded-full bg-muted text-xs ${getPostTypeColor()}`}>
            <Icon name={getPostTypeIcon()} size={12} />
            <span>{getPostTypeLabel()}</span>
          </div>
        </div>

        {/* Post Content */}
        <div className="space-y-4">
          {post?.title && (
            <h3 className="text-lg font-semibold text-foreground">
              {post?.title}
            </h3>
          )}
          
          <div className="text-foreground whitespace-pre-wrap">
            {post?.content}
          </div>

          {/* Location for performance announcements */}
          {post?.location && (
            <div className="flex items-center space-x-2 text-muted-foreground">
              <Icon name="MapPin" size={16} />
              <span className="text-sm">{post?.location}</span>
            </div>
          )}

          {/* Tags */}
          {post?.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {post?.tags?.slice(0, 5)?.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center text-xs px-2 py-1 bg-accent/10 text-accent border border-accent/20 rounded-full hover:bg-accent/20 cursor-pointer"
                >
                  #{tag}
                </span>
              ))}
              {post?.tags?.length > 5 && (
                <span className="text-xs text-muted-foreground px-2 py-1">
                  +{post?.tags?.length - 5} de plus
                </span>
              )}
            </div>
          )}
        </div>

        {/* Media placeholder for future implementation */}
        {post?.media_urls?.length > 0 && (
          <div className="mt-4 p-4 bg-muted rounded-lg border border-border">
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              <div className="text-center">
                <Icon name="Image" size={32} className="mx-auto mb-2" />
                <p className="text-sm">Médias ({post?.media_urls?.length})</p>
              </div>
            </div>
          </div>
        )}

        {/* Engagement Stats */}
        {(post?.like_count > 0 || post?.comment_count > 0) && (
          <div className="flex items-center justify-between pt-4 border-t border-border mt-4">
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              {post?.like_count > 0 && (
                <div className="flex items-center space-x-1">
                  <div className="flex -space-x-1">
                    <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center border border-background">
                      <Icon name="Heart" size={10} color="white" />
                    </div>
                  </div>
                  <span>{post?.like_count} j'aime</span>
                </div>
              )}
              {post?.comment_count > 0 && (
                <span>{post?.comment_count} commentaire{post?.comment_count > 1 ? 's' : ''}</span>
              )}
            </div>
            {post?.share_count > 0 && (
              <span className="text-sm text-muted-foreground">
                {post?.share_count} partage{post?.share_count > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-1 pt-4 border-t border-border mt-4">
          <Button
            variant="ghost"
            size="sm"
            className={`flex-1 ${isLiked ? 'text-red-500' : 'text-muted-foreground hover:text-foreground'}`}
            onClick={() => handleInteractionClick('like')}
            iconName="Heart"
          >
            J'aime
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 text-muted-foreground hover:text-foreground"
            onClick={() => setShowComments(!showComments)}
            iconName="MessageCircle"
          >
            Commenter
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className="flex-1 text-muted-foreground hover:text-foreground"
            iconName="Share"
          >
            Partager
          </Button>

          {post?.post_type === 'achievement' && (
            <Button
              variant="ghost"
              size="sm"
              className="flex-1 text-muted-foreground hover:text-foreground"
              onClick={() => handleInteractionClick('celebrate')}
              iconName="Trophy"
            >
              Féliciter
            </Button>
          )}
        </div>

        {/* Comments Section */}
        {showComments && (
          <div className="pt-4 border-t border-border mt-4 space-y-4">
            {/* Add Comment */}
            <div className="flex space-x-3">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-primary-foreground text-xs font-semibold">
                  {currentUser?.email?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1">
                <div className="flex space-x-2">
                  <input
                    type="text"
                    placeholder="Ajouter un commentaire..."
                    value={comment}
                    onChange={(e) => setComment(e?.target?.value)}
                    className="flex-1 px-3 py-2 bg-background border border-input rounded-full text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <Button
                    size="sm"
                    disabled={!comment?.trim()}
                    className="rounded-full"
                  >
                    <Icon name="Send" size={14} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Comments placeholder */}
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="MessageCircle" size={24} className="mx-auto mb-2 opacity-50" />
              <p className="text-sm">Soyez le premier à commenter</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedPost;